/**
 * @file api-product-reviews.ts
 * @description Клиент `/admin/reviews` — модерация клиентских отзывов на
 * товары (phase19.10).
 *
 * Зеркало apps/api/src/reviews/admin/admin-reviews.controller.ts.
 * Эндпоинта правки текста/оценки/фото отзыва нет намеренно — R3
 * docs/business/reviews.md: админ только модерирует, отвечает и обезличивает.
 * Ручные витринные отзывы («Свидетельства») живут в `siteReviews`
 * (lib/api-site-pages.ts) — это другая сущность.
 */

import { apiFetch, apiFetchWithMeta } from "./api-client";

// ─── Типы ────────────────────────────────────────────────────────────────

export type ProductReviewStatus = "PENDING" | "PUBLISHED" | "REJECTED" | "HIDDEN";

export interface AdminProductReviewPhoto {
  id: string;
  url: string;
}

/** Отзыв в очереди модерации (зеркало AdminProductReviewDto бэка). */
export interface AdminProductReview {
  id: string;
  status: string;
  rating: number;
  body: string;
  rejectReason: string | null;
  shopReply: string | null;
  shopRepliedAt: string | null;
  /** R2 — товар есть в доставленном заказе автора («Купил здесь»). */
  isVerified: boolean;
  photos: AdminProductReviewPhoto[];
  author: {
    name: string;
    customerId: string;
    email: string | null;
    ordersCount: number;
  };
  product: { id: string; slug: string; name: string };
  /** R6 — автоподсветка стоп-листа: подсказка модератору, не блокировка. */
  flags: string[];
  isEdited: boolean;
  createdAt: string;
  publishedAt: string | null;
}

export type ReviewCounts = Record<ProductReviewStatus, number> & {
  /** Средняя оценка по всем опубликованным отзывам (считает БД); null — их нет. */
  averageRating: number | null;
};

/** Состояние запроса отзыва по заказу (R12-R14 docs/business/reviews.md). */
export interface ReviewRequestEntry {
  id: string;
  attempt: number;
  sentAt: string;
  expiresAt: string;
  usedAt: string | null;
  convertedAt: string | null;
  sentBy: string | null;
}

export interface ReviewRequestAvailability {
  canSend: boolean;
  /** Причина, по которой кнопка недоступна; null — можно отправлять. */
  reason: string | null;
  attemptsUsed: number;
  maxAttempts: number;
  history: ReviewRequestEntry[];
}

/** Запись audit-лога модерации. */
export interface ReviewHistoryEntry {
  id: string;
  action: string;
  comment: string | null;
  actor: string | null;
  createdAt: string;
}

export interface ReviewListParams {
  status?: ProductReviewStatus;
  productId?: string;
  customerId?: string;
  rating?: number;
  withPhotos?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ReviewListResult {
  items: AdminProductReview[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Справочники подписей ────────────────────────────────────────────────

export const REVIEW_STATUS_LABEL: Record<ProductReviewStatus, string> = {
  PENDING: "На проверке",
  PUBLISHED: "Опубликован",
  REJECTED: "Отклонён",
  HIDDEN: "Скрыт",
};

/** Тона бейджей статусов (варианты компонента Badge). */
export const REVIEW_STATUS_TONE: Record<
  ProductReviewStatus,
  "warning" | "primary" | "destructive" | "default"
> = {
  PENDING: "warning",
  PUBLISHED: "primary",
  REJECTED: "destructive",
  HIDDEN: "default",
};

/** Подписи автофлагов стоп-листа (util/review-stop-list.ts на бэке). */
export const REVIEW_FLAG_LABEL: Record<string, string> = {
  MEDICAL: "Лечебные заявления",
  CONTACTS: "Контакты",
  LINKS: "Ссылки",
  PROFANITY: "Ненормативная лексика",
};

/** Пресеты причин отклонения; последняя опция в UI — «Своя причина…». */
export const REJECT_REASON_PRESETS: string[] = [
  "Лечебные заявления",
  "Контакты или ссылки",
  "Не относится к товару",
  "Ненормативная лексика",
  "Фото не соответствует",
];

// ─── Клиент ──────────────────────────────────────────────────────────────

function buildListQuery(params: ReviewListParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.productId) search.set("productId", params.productId);
  if (params.customerId) search.set("customerId", params.customerId);
  if (params.rating) search.set("rating", String(params.rating));
  if (params.withPhotos) search.set("withPhotos", "true");
  if (params.search) search.set("search", params.search);
  if (params.page) search.set("page", String(params.page));
  if (params.limit) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const productReviews = {
  /** Очередь модерации; total берём из `meta` (серверная пагинация). */
  list: async (params: ReviewListParams = {}): Promise<ReviewListResult> => {
    const { data, meta } = await apiFetchWithMeta<
      AdminProductReview[],
      { total: number; page: number; pageSize: number }
    >(`/admin/reviews${buildListQuery(params)}`);
    return {
      items: data ?? [],
      total: meta?.total ?? 0,
      page: meta?.page ?? 1,
      pageSize: meta?.pageSize ?? (params.limit ?? 20),
    };
  },

  counts: () => apiFetch<ReviewCounts>("/admin/reviews/counts"),

  getOne: (id: string) => apiFetch<AdminProductReview>(`/admin/reviews/${id}`),

  history: (id: string) =>
    apiFetch<ReviewHistoryEntry[]>(`/admin/reviews/${id}/history`),

  /** reason обязателен при REJECTED — R3 (клиенту показывается причина). */
  setStatus: (id: string, status: ProductReviewStatus, reason?: string) =>
    apiFetch<AdminProductReview>(`/admin/reviews/${id}/status`, {
      method: "PATCH",
      json: reason ? { status, reason } : { status },
    }),

  /** Пустая строка — удалить публичный ответ магазина. */
  setReply: (id: string, reply: string) =>
    apiFetch<AdminProductReview>(`/admin/reviews/${id}/reply`, {
      method: "PATCH",
      json: { reply },
    }),

  /** R8 — отзыв согласия на распространение ПДн: имя → «Покупатель». */
  anonymize: (id: string) =>
    apiFetch<AdminProductReview>(`/admin/reviews/${id}/anonymize`, {
      method: "POST",
    }),
};

/**
 * Запрос отзыва по заказу (R12-R14). Живёт под префиксом заказов, потому что
 * это действие карточки заказа, а не очереди модерации.
 */
export const reviewRequests = {
  availability: (orderId: string) =>
    apiFetch<ReviewRequestAvailability>(`/admin/orders/${orderId}/review-request`),

  send: (orderId: string) =>
    apiFetch<ReviewRequestAvailability>(`/admin/orders/${orderId}/review-request`, {
      method: "POST",
    }),
};
